import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { usePermissionCheck } from '../../contexts/PermissionContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import PermissionGuard, { EditButton, DeleteButton } from '../../components/permissions/PermissionGuard'
import apiService from '../../services/api'
import {
  HiOutlineFolder,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowDownTray,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineUser,
  HiOutlineCalendarDays,
  HiOutlineDocument,
  HiOutlineXMark,
  HiOutlineFunnel,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
} from 'react-icons/hi2'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

export default function CasesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { canCreate } = usePermissionCheck()
  const { getEntity } = useWorkspace()

  const [cases, setCases] = useState([])
  const [pageLoading, setPageLoading] = useState(true)
  const [casesLoading, setCasesLoading] = useState(false)
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [isSearching, setIsSearching] = useState(false)
  const [selectedCases, setSelectedCases] = useState([])
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false, title: '', message: '', onConfirm: null, type: 'warning',
  })

  const [filters, setFilters] = useState({
    title: searchParams.get('title') || '',
    client: searchParams.get('client') || '',
    date_after: searchParams.get('date_after') || '',
    date_before: searchParams.get('date_before') || '',
  })
  const [sortBy, setSortBy] = useState(searchParams.get('ordering') || '-created_at')
  const [showFilters, setShowFilters] = useState(false)

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get('page_size')) || 20)

  // Debounced search
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== searchQuery) {
        setSearchQuery(searchInput)
        setCurrentPage(1)
      }
      setIsSearching(false)
    }, 500)
    if (searchInput !== searchQuery && searchInput.length > 0) setIsSearching(true)
    return () => clearTimeout(id)
  }, [searchInput, searchQuery])

  // Fetch
  useEffect(() => {
    const isFirstLoad = pageLoading && cases.length === 0
    fetchCases(isFirstLoad)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, filters, sortBy, itemsPerPage, ownerAccountId])

  // Sync URL
  useEffect(() => {
    const params = new URLSearchParams()
    if (searchQuery) params.set('search', searchQuery)
    if (filters.title) params.set('title', filters.title)
    if (filters.client) params.set('client', filters.client)
    if (filters.date_after) params.set('date_after', filters.date_after)
    if (filters.date_before) params.set('date_before', filters.date_before)
    if (sortBy !== '-created_at') params.set('ordering', sortBy)
    if (currentPage > 1) params.set('page', currentPage)
    if (itemsPerPage !== 20) params.set('page_size', itemsPerPage)
    setSearchParams(params)
  }, [searchQuery, filters, sortBy, currentPage, itemsPerPage, setSearchParams])

  async function fetchCases(isInitialLoad = false) {
    try {
      if (isInitialLoad) setPageLoading(true)
      else setCasesLoading(true)

      const params = { page: currentPage, page_size: itemsPerPage }
      if (searchQuery) params.search = searchQuery
      if (filters.title) params.title = filters.title
      if (filters.client) params.client = filters.client
      if (filters.date_after) params.date_after = filters.date_after
      if (filters.date_before) params.date_before = filters.date_before
      if (sortBy) params.ordering = sortBy

      const response = await apiService.get('/cases/', { params })
      if (response.success) {
        const data = response.data.data || response.data
        const arr = data.data || data.results || []
        const pagination = data.pagination || {}
        setCases(arr)
        setTotalCount(pagination.count || arr.length)
        setTotalPages(pagination.total_pages || Math.ceil((pagination.count || arr.length) / itemsPerPage))
      } else {
        showError(response.error?.message || `Failed to load ${getEntity('case').plural.toLowerCase()}`)
      }
    } catch {
      showError(`Failed to load ${getEntity('case').plural.toLowerCase()}`)
    } finally {
      if (isInitialLoad) setPageLoading(false)
      else setCasesLoading(false)
    }
  }

  async function handleDeleteCase(caseId) {
    try {
      setConfirmDialog({ isOpen: false })
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.delete(`/cases/${caseId}/`, { params })
      if (response.success) {
        setCases(prev => prev.filter(c => c.id !== caseId))
        setSelectedCases(prev => prev.filter(id => id !== caseId))
        showSuccess(`${getEntity('case').singular} deleted successfully`)
      } else {
        showError(response.error?.message || `Failed to delete ${getEntity('case').singular.toLowerCase()}`)
      }
    } catch {
      showError(`Failed to delete ${getEntity('case').singular.toLowerCase()}`)
    }
  }

  const confirmDeleteCase = (case_) => {
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${getEntity('case').singular}`,
      message: `Are you sure you want to delete "${case_.title}"? This action cannot be undone and will remove all associated data including ${getEntity('session').plural.toLowerCase()} and ${getEntity('document').plural.toLowerCase()}.`,
      type: 'danger',
      confirmText: `Delete ${getEntity('case').singular}`,
      onConfirm: () => handleDeleteCase(case_.id),
    })
  }

  const handleBulkDelete = () => {
    if (selectedCases.length === 0) return
    setConfirmDialog({
      isOpen: true,
      title: `Delete ${selectedCases.length} ${getEntity('case').plural}`,
      message: `Are you sure you want to delete ${selectedCases.length} ${selectedCases.length === 1 ? getEntity('case').singular.toLowerCase() : getEntity('case').plural.toLowerCase()}? This cannot be undone.`,
      type: 'danger',
      confirmText: `Delete ${selectedCases.length}`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false })
        try {
          await Promise.all(selectedCases.map(caseId => {
            const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
            return apiService.delete(`/cases/${caseId}/`, { params })
          }))
          setCases(prev => prev.filter(c => !selectedCases.includes(c.id)))
          setSelectedCases([])
          showSuccess(`${selectedCases.length} ${selectedCases.length === 1 ? getEntity('case').singular.toLowerCase() : getEntity('case').plural.toLowerCase()} deleted`)
        } catch {
          showError(`Failed to delete some ${getEntity('case').plural.toLowerCase()}`)
        }
      },
    })
  }

  const toggleSelect = (id) => setSelectedCases(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleSelectAll = () => setSelectedCases(selectedCases.length === cases.length ? [] : cases.map(c => c.id))

  const clearFilters = () => {
    setFilters({ title: '', client: '', date_after: '', date_before: '' })
    setSearchInput(''); setSearchQuery(''); setSortBy('-created_at'); setCurrentPage(1)
  }

  const hasActiveFilters = !!(searchQuery || filters.title || filters.client || filters.date_after || filters.date_before)
  const activeFilterCount = (searchQuery ? 1 : 0) + Object.values(filters).filter(Boolean).length

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })

  if (pageLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
          <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineFolder}
        iconTone="info"
        title={t('cases.title', { entity: getEntity('case').plural })}
        subtitle={isWorkspaceSwitched
          ? t('cases.subtitleSwitched', { entity: getEntity('case').plural.toLowerCase(), name: user?.switchedAccountName })
          : t('cases.subtitle', { entity: getEntity('case').plural.toLowerCase(), client: getEntity('client').singular.toLowerCase() })}
        actions={
          <>
            <PermissionGuard resource="case" action="read">
              <Button variant="secondary" leadingIcon={HiOutlineArrowDownTray} onClick={() => { /* TODO */ }}>
                {t('common.export')}
              </Button>
            </PermissionGuard>
            <PermissionGuard resource="case" action="create">
              <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/cases/create')}>
                {t('cases.addCase', { entity: getEntity('case').singular })}
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <Card padding="none">
        {/* Search + sort + filter toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 p-4 sm:p-5 border-b border-border-subtle">
          <div className="flex-1 max-w-2xl">
            <Input
              leadingIcon={HiOutlineMagnifyingGlass}
              placeholder={t('cases.searchPlaceholder', { entity: getEntity('case').plural.toLowerCase(), client: getEntity('client').singular.toLowerCase() })}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              trailingAction={
                isSearching ? <Spinner size="sm" className="me-2" />
                : searchInput ? (
                  <IconButton
                    icon={HiOutlineXMark}
                    aria-label={t('common.clearSearch')}
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSearchInput(''); setSearchQuery('') }}
                  />
                ) : null
              }
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1) }}
              className="min-w-[10rem]"
            >
              <option value="-created_at">{t('sort.newest')}</option>
              <option value="created_at">{t('sort.oldest')}</option>
              <option value="title">{t('sort.titleAsc')}</option>
              <option value="-title">{t('sort.titleDesc')}</option>
              <option value="date">{t('sort.dateAsc')}</option>
              <option value="-date">{t('sort.dateDesc')}</option>
            </Select>

            <Button
              variant={showFilters || hasActiveFilters ? 'secondary' : 'ghost'}
              leadingIcon={HiOutlineFunnel}
              onClick={() => setShowFilters(v => !v)}
            >
              {t('common.filters')}
              {hasActiveFilters && (
                <Badge tone="brand" size="sm" className="ms-1">{activeFilterCount}</Badge>
              )}
            </Button>
          </div>

          {selectedCases.length > 0 && (
            <div className="flex items-center gap-2 ps-3 border-s border-border-subtle">
              <Badge tone="brand">{t('common.selected', { count: selectedCases.length })}</Badge>
              <PermissionGuard resource="case" action="delete">
                <Button variant="danger" size="sm" leadingIcon={HiOutlineTrash} onClick={handleBulkDelete}>
                  {t('common.delete')}
                </Button>
              </PermissionGuard>
            </div>
          )}
        </div>

        {/* Advanced filters */}
        {showFilters && (
          <div className="p-4 sm:p-5 bg-muted/40 border-b border-border-subtle">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <FormField label={t('cases.filterTitle')}>
                <Input
                  value={filters.title}
                  onChange={(e) => { setFilters(f => ({ ...f, title: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('cases.filterTitle')}
                />
              </FormField>
              <FormField label={t('cases.filterClient', { client: getEntity('client').singular })}>
                <Input
                  value={filters.client}
                  onChange={(e) => { setFilters(f => ({ ...f, client: e.target.value })); setCurrentPage(1) }}
                  placeholder={t('cases.filterClient', { client: getEntity('client').singular })}
                />
              </FormField>
              <FormField label={t('filters.dateFrom')}>
                <Input
                  type="date"
                  value={filters.date_after}
                  onChange={(e) => { setFilters(f => ({ ...f, date_after: e.target.value })); setCurrentPage(1) }}
                />
              </FormField>
              <FormField label={t('filters.dateTo')}>
                <Input
                  type="date"
                  value={filters.date_before}
                  onChange={(e) => { setFilters(f => ({ ...f, date_before: e.target.value })); setCurrentPage(1) }}
                />
              </FormField>
            </div>
            {hasActiveFilters && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-ink-muted">{t('filters.results', { count: totalCount })}</span>
                <Button variant="link" size="sm" onClick={clearFilters}>{t('common.clearAllFilters')}</Button>
              </div>
            )}
          </div>
        )}

        {/* List */}
        <div className="relative">
          {casesLoading && (
            <div className="absolute inset-0 bg-surface/70 backdrop-blur-sm flex items-center justify-center z-10 rounded-b-xl">
              <div className="inline-flex items-center gap-2">
                <Spinner size="sm" />
                <span className="text-sm text-ink-secondary">{t('common.loading')}</span>
              </div>
            </div>
          )}

          {cases.length === 0 ? (
            <EmptyState
              icon={HiOutlineFolder}
              title={t('cases.noFound', { entity: getEntity('case').plural.toLowerCase() })}
              description={
                searchQuery
                  ? t('cases.noMatchSearch', { entity: getEntity('case').plural.toLowerCase(), query: searchQuery })
                  : canCreate('case')
                    ? t('cases.getStarted', { entity: getEntity('case').singular.toLowerCase() })
                    : t('cases.noAvailable', { entity: getEntity('case').plural.toLowerCase() })
              }
              action={
                !searchQuery && (
                  <PermissionGuard resource="case" action="create">
                    <Button leadingIcon={HiOutlinePlus} onClick={() => navigate('/cases/create')}>
                      {t('cases.createFirst', { entity: getEntity('case').singular.toLowerCase() })}
                    </Button>
                  </PermissionGuard>
                )
              }
            />
          ) : (
            <>
              <div className="px-4 sm:px-5 py-2.5 border-b border-border-subtle bg-muted/40">
                <Checkbox
                  checked={selectedCases.length === cases.length && cases.length > 0}
                  onChange={toggleSelectAll}
                  disabled={casesLoading}
                >
                  {t('cases.selectAll', {
                    count: cases.length,
                    entity: cases.length !== 1 ? getEntity('case').plural.toLowerCase() : getEntity('case').singular.toLowerCase(),
                  })}
                </Checkbox>
              </div>

              <div className={`p-4 sm:p-5 transition-opacity ${casesLoading ? 'opacity-50' : ''}`}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {cases.map((case_) => (
                    <CaseCard
                      key={case_.id}
                      case_={case_}
                      selected={selectedCases.includes(case_.id)}
                      onToggle={() => toggleSelect(case_.id)}
                      onEdit={() => navigate(`/cases/${case_.id}/edit`)}
                      onDelete={() => confirmDeleteCase(case_)}
                      onView={() => navigate(`/cases/${case_.id}`)}
                      formatDate={formatDate}
                      getEntity={getEntity}
                      disabled={casesLoading}
                    />
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 sm:p-5 border-t border-border-subtle bg-muted/30 transition-opacity ${casesLoading ? 'opacity-50' : ''}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-ink-muted">
                    {t('pagination.showing')}{' '}
                    <span className="font-semibold text-ink-primary">
                      {cases.length === 0 ? 0 : ((currentPage - 1) * itemsPerPage) + 1}
                    </span>{' '}
                    –{' '}
                    <span className="font-semibold text-ink-primary">
                      {Math.min(currentPage * itemsPerPage, totalCount)}
                    </span>{' '}
                    {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalCount}</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-ink-muted">{t('pagination.show')}:</span>
                    <Select
                      size="sm"
                      value={itemsPerPage}
                      onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1) }}
                      disabled={casesLoading}
                      className="w-auto"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Select>
                  </div>
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    disabled={casesLoading}
                    onChange={setCurrentPage}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </Card>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  )
}

function CaseCard({ case_, selected, onToggle, onEdit, onDelete, onView, formatDate, getEntity, disabled }) {
  const { t } = useTranslation()
  return (
    <div className="bg-surface border border-border rounded-xl p-4 hover:border-border-strong hover:shadow-token-sm transition-all">
      <div className="flex items-start gap-3 mb-3">
        <div className="pt-1">
          <Checkbox checked={selected} onChange={onToggle} disabled={disabled} aria-label={t('common.selectAll')} />
        </div>
        <span className="w-10 h-10 bg-info-soft text-info rounded-xl flex items-center justify-center flex-shrink-0">
          <HiOutlineFolder className="w-5 h-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-ink-primary truncate">{case_.title}</h3>
          <p className="text-xs text-ink-muted">{t('cases.created', { date: formatDate(case_.created_at) })}</p>
        </div>
      </div>

      <dl className="space-y-1.5 text-sm text-ink-secondary mb-3">
        <div className="flex items-center gap-2">
          <HiOutlineUser className="w-4 h-4 text-ink-muted" />
          <span className="truncate">{case_.client_name || t('cases.unknownClient', { entity: getEntity('client').singular })}</span>
        </div>
        <div className="flex items-center gap-2">
          <HiOutlineCalendarDays className="w-4 h-4 text-ink-muted" />
          <span>{formatDate(case_.date)}</span>
        </div>
        {case_.description && (
          <p className="text-ink-muted line-clamp-2">{case_.description}</p>
        )}
        <div className="flex items-center gap-2">
          <HiOutlineDocument className="w-4 h-4 text-ink-muted" />
          <span>{t('cases.sessionsCount', { count: case_.sessions_count || 0, entity: getEntity('session').plural.toLowerCase() })}</span>
        </div>
      </dl>

      <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
        <Button variant="link" size="sm" onClick={onView} disabled={disabled}>
          {t('common.viewDetails')}
        </Button>
        <div className="flex items-center gap-1">
          <EditButton resource="case">
            <IconButton
              icon={HiOutlinePencil}
              aria-label={t('common.edit')}
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={disabled}
            />
          </EditButton>
          <DeleteButton resource="case">
            <IconButton
              icon={HiOutlineTrash}
              aria-label={t('common.delete')}
              variant="danger"
              size="sm"
              onClick={onDelete}
              disabled={disabled}
            />
          </DeleteButton>
        </div>
      </div>
    </div>
  )
}

function Pagination({ currentPage, totalPages, disabled, onChange }) {
  const { t } = useTranslation()
  const maxVisible = 5
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)
  if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1)
  const pages = []
  for (let i = startPage; i <= endPage; i++) pages.push(i)

  return (
    <div className="flex items-center gap-1">
      <IconButton
        icon={HiOutlineChevronDoubleLeft}
        aria-label={t('pagination.firstPage')}
        variant="ghost"
        size="sm"
        className="rtl:rotate-180"
        disabled={currentPage === 1 || disabled}
        onClick={() => onChange(1)}
      />
      <IconButton
        icon={HiOutlineChevronLeft}
        aria-label={t('pagination.previousPage')}
        variant="ghost"
        size="sm"
        className="rtl:rotate-180"
        disabled={currentPage === 1 || disabled}
        onClick={() => onChange(Math.max(1, currentPage - 1))}
      />
      {pages.map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          disabled={disabled}
          className={`min-w-[2rem] h-8 px-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
            i === currentPage
              ? 'bg-brand text-white'
              : 'text-ink-secondary hover:bg-muted'
          }`}
        >
          {i}
        </button>
      ))}
      {currentPage < totalPages - 2 && (
        <>
          <span className="px-1 text-ink-muted text-sm">…</span>
          <button
            type="button"
            onClick={() => onChange(totalPages)}
            disabled={disabled}
            className="min-w-[2rem] h-8 px-2 text-sm font-medium text-ink-secondary hover:bg-muted rounded-md disabled:opacity-50"
          >
            {totalPages}
          </button>
        </>
      )}
      <IconButton
        icon={HiOutlineChevronRight}
        aria-label={t('pagination.nextPage')}
        variant="ghost"
        size="sm"
        className="rtl:rotate-180"
        disabled={currentPage === totalPages || disabled}
        onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
      />
      <IconButton
        icon={HiOutlineChevronDoubleRight}
        aria-label={t('pagination.lastPage')}
        variant="ghost"
        size="sm"
        className="rtl:rotate-180"
        disabled={currentPage === totalPages || disabled}
        onClick={() => onChange(totalPages)}
      />
    </div>
  )
}
